<template>
  <!--
    Ce bloc n'apparaît que pour un exécutif qui gouverne la commune de l'équipement : le backend
    renvoie 403 sinon, et afficher des actions inopérantes serait pire que de ne rien montrer.
  -->
  <div v-if="analyse" class="GouvernancePanel flex flex-col gap-8">
    <!-- §4.1 et §4.2 — analyse spatiale, également le bloc « Analyse spatiale » du §3.5.3 -->
    <section class="GouvernancePanel__section">
      <h2 class="GouvernancePanel__titre">{{ t('infrastructure.gouvernance.analyseSpatiale') }}</h2>

      <h3 class="GouvernancePanel__sousTitre">{{ t('infrastructure.gouvernance.proximite') }}</h3>
      <ul v-if="analyse.proximite.length > 0" class="flex flex-col gap-1.5">
        <li
          v-for="voisin in analyse.proximite"
          :key="voisin.iftId"
          class="flex flex-wrap items-baseline gap-x-3 text-sm"
        >
          <span class="w-20 shrink-0 tabular-nums font-medium text-gray-900">
            {{ voisin.distanceMetres }} m
          </span>
          <span class="text-gray-600">{{ voisin.typeLibelle }}</span>
          <span class="text-gray-900">{{ voisin.nom }}</span>
        </li>
      </ul>
      <p v-else class="text-sm italic text-gray-500">{{ t('infrastructure.gouvernance.aucunVoisin') }}</p>

      <h3 class="GouvernancePanel__sousTitre">{{ t('infrastructure.gouvernance.chalandise') }}</h3>
      <div class="flex flex-wrap items-center gap-3">
        <label class="text-sm text-gray-700" for="gouvernance-rayon">
          {{ t('infrastructure.gouvernance.rayon') }}
        </label>
        <select
          id="gouvernance-rayon"
          v-model.number="rayon"
          class="rounded border border-gray-300 px-2 py-1 text-sm"
          @change="chargerAnalyse"
        >
          <option v-for="valeur in RAYONS" :key="valeur" :value="valeur">{{ valeur }} m</option>
        </select>
        <p class="text-sm text-gray-600">
          {{
            t(
              'infrastructure.gouvernance.chalandiseResume',
              {
                total: analyse.chalandise.total,
                surface: analyse.chalandise.surfaceHectares,
              },
              analyse.chalandise.total,
            )
          }}
        </p>
      </div>
      <ul v-if="analyse.chalandise.typologies.length > 0" class="mt-2 flex flex-wrap gap-2">
        <li
          v-for="typologie in analyse.chalandise.typologies"
          :key="typologie.code"
          class="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700"
        >
          {{ typologie.libelle }} · {{ typologie.nombre }}
        </li>
      </ul>
    </section>

    <!-- §5 — décision du maire -->
    <section class="GouvernancePanel__section">
      <h2 class="GouvernancePanel__titre">{{ t('infrastructure.gouvernance.decision.titre') }}</h2>

      <div class="flex flex-wrap gap-2">
        <button
          v-for="option in DECISIONS"
          :key="option.statut"
          type="button"
          class="rounded border px-3 py-1.5 text-sm transition-colors"
          :class="
            statutChoisi === option.statut
              ? 'border-transparent bg-main text-white'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
          "
          :aria-pressed="statutChoisi === option.statut"
          @click="statutChoisi = option.statut"
        >
          {{ t(option.cle) }}
        </button>
      </div>

      <div v-if="motifRequis" class="mt-3">
        <label class="mb-1 block text-sm font-medium text-gray-700" for="gouvernance-motif">
          {{ t('infrastructure.gouvernance.decision.motif') }} <span class="text-red-600">*</span>
        </label>
        <textarea
          id="gouvernance-motif"
          v-model="motif"
          rows="3"
          maxlength="2000"
          class="w-full rounded border border-gray-300 p-2 text-sm"
          :placeholder="t('infrastructure.gouvernance.decision.motifPlaceholder')"
        />
      </div>

      <p
        v-if="messageDecision"
        class="mt-2 text-sm"
        :class="decisionEnErreur ? 'text-red-700' : 'text-green-700'"
      >
        {{ messageDecision }}
      </p>

      <div class="mt-3 flex flex-wrap gap-3">
        <button
          type="button"
          class="rounded bg-main px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
          :disabled="!statutChoisi || enregistrement || (motifRequis && motif.trim() === '')"
          @click="enregistrerDecision"
        >
          {{
            enregistrement ? t('infrastructure.gouvernance.decision.enregistrement') : t('infrastructure.gouvernance.decision.enregistrer')
          }}
        </button>

        <button
          type="button"
          class="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-40"
          :disabled="pdfEnCours"
          @click="exporterPdf"
        >
          {{ pdfEnCours ? t('infrastructure.gouvernance.rapport.generation') : t('infrastructure.gouvernance.rapport.exporter') }}
        </button>
      </div>
    </section>

    <!-- §5 — registre des interventions -->
    <section class="GouvernancePanel__section">
      <h2 class="GouvernancePanel__titre">{{ t('infrastructure.gouvernance.interventions.titre') }}</h2>

      <table v-if="interventions.length > 0" class="w-full text-sm">
        <thead>
          <tr class="border-b border-gray-300 text-left text-xs uppercase text-gray-500">
            <th class="py-2 pr-4 font-medium">{{ t('infrastructure.gouvernance.interventions.tache') }}</th>
            <th class="py-2 pr-4 font-medium">{{ t('infrastructure.gouvernance.interventions.statut') }}</th>
            <th class="py-2 pr-4 font-medium">{{ t('infrastructure.gouvernance.interventions.prevue') }}</th>
            <th class="py-2 font-medium">{{ t('infrastructure.gouvernance.interventions.responsable') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in interventions"
            :key="item['@id'] ?? index"
            class="border-b border-gray-100"
          >
            <td class="py-2 pr-4 text-gray-900">{{ item.itvTypeTache }}</td>
            <td class="py-2 pr-4 text-gray-700">
              {{ t(`infrastructure.gouvernance.interventions.statuts.${item.itvStatut}`) }}
            </td>
            <td class="py-2 pr-4 tabular-nums text-gray-700">
              {{ formatDate(item.itvDatePrevue) }}
            </td>
            <td class="py-2 text-gray-700">{{ item.itvResponsable || '—' }}</td>
          </tr>
        </tbody>
      </table>
      <p v-else class="text-sm italic text-gray-500">{{ t('infrastructure.gouvernance.interventions.aucune') }}</p>

      <form class="mt-4 grid gap-3 sm:grid-cols-2" @submit.prevent="programmer">
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700" for="itv-tache">
            {{ t('infrastructure.gouvernance.interventions.tache') }}
          </label>
          <input
            id="itv-tache"
            v-model="nouvelleTache"
            type="text"
            required
            maxlength="255"
            class="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
            :placeholder="t('infrastructure.gouvernance.interventions.tachePlaceholder')"
          />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700" for="itv-responsable">
            {{ t('infrastructure.gouvernance.interventions.responsable') }}
          </label>
          <input
            id="itv-responsable"
            v-model="nouveauResponsable"
            type="text"
            maxlength="255"
            class="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
            :placeholder="t('infrastructure.gouvernance.interventions.responsablePlaceholder')"
          />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700" for="itv-date">
            {{ t('infrastructure.gouvernance.interventions.datePrevue') }}
          </label>
          <input
            id="itv-date"
            v-model="nouvelleDate"
            type="date"
            class="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
          />
        </div>
        <div class="flex items-end">
          <button
            type="submit"
            class="rounded bg-main px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
            :disabled="programmation || nouvelleTache.trim() === ''"
          >
            {{
              programmation
                ? t('infrastructure.gouvernance.decision.enregistrement')
                : t('infrastructure.gouvernance.interventions.programmer')
            }}
          </button>
        </div>
      </form>

      <p v-if="messageIntervention" class="mt-2 text-sm text-red-700">{{ messageIntervention }}</p>
    </section>
  </div>
</template>

<script setup lang="ts">
import {
  InterventionStatut,
  STATUTS_EXIGEANT_UN_MOTIF,
  ValidationStatut,
  type AnalyseSpatiale,
  type Intervention,
} from '@/models/interfaces/dashboard/Gouvernance'
import { GouvernanceService } from '@/services/dashboard/GouvernanceService'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{ iftId: number | null }>()

const { t, locale } = useI18n()

const RAYONS = [250, 500, 1000, 2000, 5000]

// Les boutons portent la clé et non le libellé : la liste est construite au chargement du module,
// donc avant tout changement de langue, et un libellé figé y resterait dans la langue de départ.
const DECISIONS = [
  { statut: ValidationStatut.VALIDEE, cle: 'infrastructure.gouvernance.decision.valider' },
  { statut: ValidationStatut.REJETEE, cle: 'infrastructure.gouvernance.decision.rejeter' },
  {
    statut: ValidationStatut.CONTRE_EXPERTISE,
    cle: 'infrastructure.gouvernance.decision.contreExpertise',
  },
]

const analyse = ref<AnalyseSpatiale | null>(null)
const interventions = ref<Intervention[]>([])
const rayon = ref(500)

const statutChoisi = ref<ValidationStatut | null>(null)
const motif = ref('')
const enregistrement = ref(false)
const messageDecision = ref('')
const decisionEnErreur = ref(false)

const pdfEnCours = ref(false)
const nouvelleTache = ref('')
const nouveauResponsable = ref('')
const nouvelleDate = ref('')
const programmation = ref(false)
const messageIntervention = ref('')

const motifRequis = computed(
  () => statutChoisi.value !== null && STATUTS_EXIGEANT_UN_MOTIF.includes(statutChoisi.value),
)

function formatDate(valeur?: string | null): string {
  if (!valeur) return '—'
  return new Date(valeur).toLocaleDateString(locale.value)
}

/**
 * Un échec est silencieux et laisse le panneau masqué : 403 signifie simplement que l'utilisateur
 * n'est pas l'exécutif de cette commune, et 409 que l'équipement est hors de tout périmètre. Ni
 * l'un ni l'autre n'est une anomalie à signaler au lecteur d'une fiche.
 */
async function chargerAnalyse(): Promise<void> {
  if (props.iftId === null) return

  try {
    analyse.value = await GouvernanceService.getAnalyseSpatiale(props.iftId, {
      rayon: rayon.value,
    })
  } catch {
    analyse.value = null
  }
}

async function chargerInterventions(): Promise<void> {
  if (props.iftId === null) return

  try {
    interventions.value = await GouvernanceService.getInterventions(props.iftId)
  } catch {
    interventions.value = []
  }
}

async function enregistrerDecision(): Promise<void> {
  if (props.iftId === null || statutChoisi.value === null) return

  enregistrement.value = true
  messageDecision.value = ''

  try {
    await GouvernanceService.decider(props.iftId, statutChoisi.value, motif.value)
    decisionEnErreur.value = false
    messageDecision.value = t('infrastructure.gouvernance.decision.enregistree')
    motif.value = ''
    statutChoisi.value = null
  } catch {
    decisionEnErreur.value = true
    messageDecision.value = t('infrastructure.gouvernance.decision.erreur')
  } finally {
    enregistrement.value = false
  }
}

async function exporterPdf(): Promise<void> {
  if (props.iftId === null) return

  pdfEnCours.value = true
  try {
    await GouvernanceService.ouvrirRapportPdf(props.iftId, { rayon: rayon.value })
  } catch {
    decisionEnErreur.value = true
    messageDecision.value = t('infrastructure.gouvernance.rapport.erreur')
  } finally {
    pdfEnCours.value = false
  }
}

async function programmer(): Promise<void> {
  if (props.iftId === null || analyse.value === null) return

  programmation.value = true
  messageIntervention.value = ''

  try {
    await GouvernanceService.programmerIntervention({
      infrastructure: `/api/infrastructures/${props.iftId}`,
      commune: analyse.value.commune['@id'],
      itvTypeTache: nouvelleTache.value.trim(),
      itvResponsable: nouveauResponsable.value.trim() || null,
      itvDatePrevue: nouvelleDate.value || null,
      itvStatut: InterventionStatut.PROGRAMMEE,
    })

    nouvelleTache.value = ''
    nouveauResponsable.value = ''
    nouvelleDate.value = ''
    await chargerInterventions()
  } catch {
    messageIntervention.value = t('infrastructure.gouvernance.interventions.erreur')
  } finally {
    programmation.value = false
  }
}

watch(
  () => props.iftId,
  () => {
    analyse.value = null
    interventions.value = []
    void chargerAnalyse()
    void chargerInterventions()
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
.GouvernancePanel {
  &__titre {
    font-size: 1.05rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
    padding-bottom: 0.35rem;
    border-bottom: 1px solid #e5e7eb;
  }

  &__sousTitre {
    font-size: 0.9rem;
    font-weight: 600;
    margin: 1rem 0 0.5rem;
  }
}
</style>
